import { MediaPlayer as dashPlayer, type MediaPlayerClass as DashPlayer } from 'dashjs';
import { getPosterFromStreamId, getSrcFromStreamId, rewriteMpdRelativeUrlsToAbsolute } from './utils';

export class VideoPlayer {
    private videoElement: HTMLVideoElement;
    private streamId: string;
    private playerType: 'dash' | 'native';
    private dashPlayer?: DashPlayer;

    constructor(videoElement: HTMLVideoElement, streamId: string) {
        this.videoElement = videoElement;
        this.streamId = streamId;
        this.playerType = this.detectPlayerType();
    }

    private detectPlayerType() {
        // Older iOS devices/versions don't support MediaSource which is used to play DASH stream files
        // If we can't detect support, we'll fall back to the native player as best we can.
        // Realistically web, android, and new ios devices will support DASH.
        let supportsDash = false;
        try {
            supportsDash = !!MediaSource?.isTypeSupported('video/mp4; codecs="avc1.42E01E"');
        } catch {
            // no-op
        }

        return supportsDash ? 'dash' : 'native';
    }

    public init(onCanPlay: () => void) {
        if (this.playerType === 'dash') {
            const dash = dashPlayer().create();

            dash.addResponseInterceptor(response => {
                // This modifies the response to ensure the segment urls are absolute
                // because relative urls become a problem in capacitor
                if (response.url?.includes('mpd') && response.data && typeof response.data === 'string') {
                    const patched = rewriteMpdRelativeUrlsToAbsolute(response.data, new URL(this.getSrc()));

                    return Promise.resolve({
                        ...response,
                        data: patched,
                    });
                }
                return Promise.resolve(response);
            });
            dash.on(dashPlayer.events.CAN_PLAY, onCanPlay);
            dash.on(dashPlayer.events.PLAYBACK_NOT_ALLOWED, () => {
                dash.setMute(true);
                dash.play();
            });
            dash.initialize(this.videoElement, this.getSrc(), false);
            dash.preload();

            this.dashPlayer = dash;
            this.videoElement.poster = getPosterFromStreamId(this.streamId);
        } else {
            this.videoElement.onloadedmetadata = onCanPlay;
            this.videoElement.oncanplay = onCanPlay;
            this.videoElement.src = this.getSrc();
            this.videoElement.preload = 'auto';
            this.videoElement.load();
            this.videoElement.poster = getPosterFromStreamId(this.streamId);
        }
    }

    public play(needsNativeLoad = false) {
        if (this.dashPlayer) {
            this.dashPlayer.play();
        } else {
            if (needsNativeLoad) this.videoElement.load();
            this.videoElement.play();
        }
    }

    public pause() {
        if (this.dashPlayer) {
            this.dashPlayer.pause();
        } else {
            this.videoElement.pause();
        }
    }

    public seek(seconds: number) {
        if (this.dashPlayer) {
            this.dashPlayer.seek(seconds);
        } else {
            this.videoElement.currentTime = seconds;
        }
    }

    public mute(muted: boolean) {
        if (this.dashPlayer) {
            this.dashPlayer.setMute(muted);
        } else {
            this.videoElement.muted = muted;
        }
    }

    public enableCaptions(enable: boolean) {
        if (this.dashPlayer) {
            this.dashPlayer.enableText(enable);
        } else {
            // Native: assume the first track is the captions track
            const tracks = this.videoElement.textTracks;
            for (let i = 0; i < tracks.length; i++) {
                tracks[i].mode = enable ? 'showing' : 'disabled';
            }
        }
    }

    public getCurrentTime() {
        return this.dashPlayer ? this.dashPlayer.time() : this.videoElement.currentTime;
    }

    public getDuration() {
        return this.dashPlayer ? this.dashPlayer.duration() : this.videoElement.duration;
    }

    public isPaused() {
        return this.dashPlayer ? this.dashPlayer.isPaused() : this.videoElement.paused;
    }

    public destroy() {
        if (this.dashPlayer) {
            this.dashPlayer.destroy();
        }
        this.videoElement.src = '';
        this.videoElement.oncanplay = null;
    }

    public getSrc() {
        if (this.playerType === 'dash') return getSrcFromStreamId(this.streamId, 'mpd');

        return getSrcFromStreamId(this.streamId, 'm3u8');
    }
}
